import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Add from '@mui/icons-material/Add';
import { MainLayout } from 'layouts/MainLayout';
import { campaignApi } from 'components/campaigns/campaignApi';
import { CampaignsTable } from 'components/campaigns/CampaignsTable';
import { CampaignStopDialog } from 'components/campaigns/modals/CampaignStopDialog';
import { Campaign, CampaignState } from 'components/campaigns/models/Campaign';
import { EmptyState } from 'components/utils/emptyState';
import {
    MESSAGE_MARKETING_CAMPAIGN_START_ERROR,
    MESSAGE_MARKETING_CAMPAIGN_START_SUCCESS,
    MESSAGE_MARKETING_CAMPAIGN_STOP_ERROR,
    MESSAGE_MARKETING_CAMPAIGN_STOP_SUCCESS
} from 'config/messages';
import { MARKETING_CAMPAIGNS_CREATE, MARKETING_CAMPAIGNS_UPDATE } from 'config/routes';
import { QueryParameter } from 'lib/HttpClient';
import logger from 'lib/logger';
import EmptyStateImage from 'static/campaigns-empty-state.svg';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { TableActionButton } from 'ui/buttons/TableActionButton';
import { MuiMenuItem } from 'ui/MenuOn';
import { MuiTableScrollItems, MuiTableToolbarItems } from 'ui/table/MuiTable';

const ITEMS_PER_PAGE = 15;

const CampaignsPage = () => {
    const [campaigns, setCampaigns] = React.useState<Campaign[]>([]);
    const [nextKey, setNextKey] = React.useState<string>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [stopCampaignId, setStopCampaignId] = React.useState(null);
    const [stopCampaignLoading, setStopCampaignLoading] = React.useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const createCamapign = React.useCallback(() => history.push(MARKETING_CAMPAIGNS_CREATE), [history]);
    const editCamapign = React.useCallback(
        (campaignId: string) => history.push(MARKETING_CAMPAIGNS_UPDATE.replace(':campaignId', campaignId)),
        [history]
    );
    const loadCampaigns = React.useCallback(
        async (newOnly: boolean) => {
            try {
                setIsLoading(true);
                const queryParameters: QueryParameter[] = [{ key: 'limit', value: ITEMS_PER_PAGE }];
                if (nextKey) {
                    queryParameters.push({ key: 'startKey', value: nextKey });
                }
                const result = await campaignApi.getList({ queryParameters });
                if (!result.ok) {
                    throw new Error(result.body.message);
                }
                setCampaigns(newOnly ? result.body.items : [...campaigns, ...result.body.items]);
                setNextKey(result.body.page.nextKey);
            } catch (e) {
                logger.error('Failed to load campaigns', e.message);
                setCampaigns([]);
            }
            setIsLoading(false);
        },
        [campaigns, nextKey]
    );
    const stopCampaign = React.useCallback(async () => {
        if (stopCampaignId === null) {
            return;
        }
        setStopCampaignLoading(true);
        const result = await campaignApi.stop(stopCampaignId);
        if (!result.ok) {
            dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_STOP_ERROR, { variant: 'error' }));
        } else {
            dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_STOP_SUCCESS, { variant: 'success' }));
            setNextKey(undefined);
            loadCampaigns(true);
        }
        setStopCampaignLoading(false);
        setStopCampaignId(null);
    }, [dispatch, stopCampaignId, loadCampaigns]);
    const startCampaign = React.useCallback(
        async (campaignId: string) => {
            const result = await campaignApi.start(campaignId);
            if (!result.ok) {
                return dispatch(
                    enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_START_ERROR, { variant: 'error' })
                );
            }
            dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_START_SUCCESS, { variant: 'success' }));
            setNextKey(undefined);
            loadCampaigns(true);
        },
        [dispatch, loadCampaigns]
    );
    React.useEffect(() => {
        setIsLoading(true);
        campaignApi
            .getList({ queryParameters: [{ key: 'limit', value: ITEMS_PER_PAGE }] })
            .then(result => {
                if (!result.ok) {
                    throw new Error(result.body.message);
                }
                setCampaigns(result.body.items || []);
                setNextKey(result.body.page.nextKey);
            })
            .catch(e => {
                logger.error('Failed to load campaigns', e.message);
                setCampaigns([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);
    const toolbarItems: MuiTableToolbarItems = {
        renderCustomElement: () => (
            <TableActionButton Icon={Add} onClick={createCamapign}>
                New Campaign
            </TableActionButton>
        )
    };
    const scroll: MuiTableScrollItems = {
        isMoreItems: !!nextKey,
        key: nextKey,
        onScroll: () => loadCampaigns(false) // onScroll passes Waypoint Arguments as a parameter
    };
    const itemActions = React.useCallback(
        (campaign: Campaign): MuiMenuItem[] => {
            const actions: MuiMenuItem[] = [];
            if (campaign.state === CampaignState.STARTED) {
                actions.push({
                    label: 'Stop',
                    onClick: () => {
                        setStopCampaignId(campaign.id);
                    }
                });
            }
            if ([CampaignState.DRAFTED, CampaignState.START_FAILED].includes(campaign.state)) {
                actions.push({
                    label: 'Edit and Review',
                    onClick: () => {
                        editCamapign(campaign.id);
                    }
                });
                actions.push({
                    label: 'Start',
                    onClick: () => {
                        startCampaign(campaign.id);
                    }
                });
            }
            return actions;
        },
        [editCamapign, startCampaign]
    );
    const closeStopDialog = React.useCallback(() => setStopCampaignId(null), []);
    const emptyStateDescription =
        'Campaigns are a great way to promote customer loyalty and increase revenue. Once you create a campaign, it will show here.';
    return (
        <MainLayout pageName="Campaigns" pageDescription=" ">
            {!isLoading && campaigns.length === 0 && (
                <EmptyState
                    headerText="Get more out of your marketing with campaigns"
                    paragraphText={emptyStateDescription}
                    buttonText="Create a campaign"
                    imageUrl={EmptyStateImage}
                    onClick={createCamapign}
                />
            )}
            {(isLoading || campaigns.length > 0) && (
                <CampaignsTable
                    data={campaigns}
                    scroll={scroll}
                    toolbarItems={toolbarItems}
                    isLoading={isLoading || !!nextKey}
                    itemActions={itemActions}
                />
            )}
            {!isLoading && (
                <CampaignStopDialog
                    open={stopCampaignId !== null}
                    loading={stopCampaignLoading}
                    onAgree={stopCampaign}
                    onClose={closeStopDialog}
                />
            )}
        </MainLayout>
    );
};

export default CampaignsPage;
